import React, { useState, useEffect, useRef } from 'react'
import { Box, IconButton, Typography, Dialog } from '@mui/material'
import {
  Close as CloseIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from '@mui/icons-material'

// Full-screen image viewer with swipe-to-navigate gesture support.
export default function ImageLightbox({ images = [], initialIndex = 0, open, onClose }) {
  const [index, setIndex] = useState(initialIndex)
  const touchStart = useRef(null)

  useEffect(() => {
    if (open) setIndex(initialIndex)
  }, [open, initialIndex])

  if (!images.length) return null

  const current = images[index]
  const hasPrev = index > 0
  const hasNext = index < images.length - 1

  const prev = (e) => { e?.stopPropagation(); if (hasPrev) setIndex(i => i - 1) }
  const next = (e) => { e?.stopPropagation(); if (hasNext) setIndex(i => i + 1) }

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX }
  const onTouchEnd = (e) => {
    if (touchStart.current === null) return
    const dx = e.changedTouches[0].clientX - touchStart.current
    touchStart.current = null
    if (dx < -50) next()
    else if (dx > 50) prev()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: '#000', overflow: 'hidden' } }}
      TransitionProps={{ unmountOnExit: true }}
    >
      {/* Header: counter + close */}
      <Box sx={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1,
        pt: 'calc(8px + env(safe-area-inset-top, 0px))',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)',
      }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', fontWeight: 600, pl: 1 }}>
          {index + 1} / {images.length}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Main image — fills screen, touch to swipe between photos */}
      <Box
        sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onClick={onClose}
      >
        <Box
          component="img"
          src={current.image}
          alt={current.description || ''}
          onClick={(e) => e.stopPropagation()}
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />
      </Box>

      {/* Prev arrow */}
      {hasPrev && (
        <IconButton
          onClick={prev}
          sx={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
            color: '#fff', bgcolor: 'rgba(0,0,0,0.45)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
          }}
        >
          <PrevIcon sx={{ fontSize: 32 }} />
        </IconButton>
      )}

      {/* Next arrow */}
      {hasNext && (
        <IconButton
          onClick={next}
          sx={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            color: '#fff', bgcolor: 'rgba(0,0,0,0.45)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
          }}
        >
          <NextIcon sx={{ fontSize: 32 }} />
        </IconButton>
      )}

      {/* Caption */}
      {current.description && (
        <Box sx={{
          position: 'absolute', bottom: 88, left: 0, right: 0,
          textAlign: 'center', px: 3, pointerEvents: 'none',
        }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem' }}>
            {current.description}
          </Typography>
        </Box>
      )}

      {/* Thumbnail strip at bottom */}
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        display: 'flex', gap: 0.75, overflowX: 'auto', px: 1.5,
        pt: 1.5,
        pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
        '&::-webkit-scrollbar': { display: 'none' },
      }}>
        {images.map((img, i) => (
          <Box
            key={img.id ?? i}
            component="img"
            src={img.image}
            alt={img.description || ''}
            onClick={(e) => { e.stopPropagation(); setIndex(i) }}
            sx={{
              width: 56, height: 56, flexShrink: 0,
              objectFit: 'cover', borderRadius: 1,
              cursor: 'pointer',
              border: i === index ? '2.5px solid #fff' : '2.5px solid transparent',
              opacity: i === index ? 1 : 0.55,
              transition: 'opacity 0.15s, border-color 0.15s',
            }}
          />
        ))}
      </Box>
    </Dialog>
  )
}
